
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Copy, 
  ExternalLink, 
  Calendar,
  Trash2,
  Share,
  Shield
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface InventoryShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentShareData?: {
    share_token: string | null;
    is_shared: boolean;
    expiration: string | null;
  };
}

export function InventoryShareDialog({ 
  open, 
  onOpenChange, 
  userId,
  currentShareData 
}: InventoryShareDialogProps) {
  const [shareUrl, setShareUrl] = useState<string>("");
  const [expiryDays, setExpiryDays] = useState<string>("never");
  const queryClient = useQueryClient();

  const generateShareMutation = useMutation({
    mutationFn: async ({ expiry }: { expiry: string }) => {
      console.log("Starting inventory share generation for user:", userId);
      
      // Generate a UUID using the browser's crypto API
      const token = crypto.randomUUID();
      
      let expirationDate = null;
      if (expiry !== "never") {
        expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + parseInt(expiry));
      }

      console.log("Generated token:", token);
      console.log("Expiration date:", expirationDate?.toISOString());

      // CRITICAL: Only create sharing records, never modify inventory items
      const { data, error } = await supabase
        .from("public_inventory_shares")
        .insert({
          share_token: token,
          project_id: null, // For user inventory, project_id can be null
          access_count: 0,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating inventory share:", error);
        throw new Error(`Failed to create share: ${error.message}`);
      }

      console.log("Successfully created inventory share:", data);

      // Check if user_inventory_shares record exists first
      const { data: existingShare } = await supabase
        .from("user_inventory_shares")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (existingShare) {
        // Update existing record - ONLY update sharing preferences, not inventory
        const { error: updateError } = await supabase
          .from("user_inventory_shares")
          .update({
            public_share_token: token,
            is_public_shared: true,
            public_share_expiration: expirationDate?.toISOString(),
            public_share_revoked: false,
          })
          .eq("user_id", userId);

        if (updateError) {
          console.error("Error updating user share preference:", updateError);
          throw new Error(`Failed to update sharing preferences: ${updateError.message}`);
        }
      } else {
        // Create new record with explicit user_id - CRITICAL: Only create sharing record
        const { error: insertError } = await supabase
          .from("user_inventory_shares")
          .insert({
            user_id: userId,  // Explicitly set the user_id to prevent cross-contamination
            public_share_token: token,
            is_public_shared: true,
            public_share_expiration: expirationDate?.toISOString(),
            public_share_revoked: false,
          });

        if (insertError) {
          console.error("Error creating user share preference:", insertError);
          throw new Error(`Failed to create sharing preferences: ${insertError.message}`);
        }
      }

      const url = `${window.location.origin}/public/inventory/${token}`;
      setShareUrl(url);
      console.log("Generated inventory share URL:", url);
      
      return { url, token };
    },
    onSuccess: () => {
      toast.success("Inventory share link generated successfully!");
      queryClient.invalidateQueries({ queryKey: ["user-inventory-share", userId] });
    },
    onError: (error) => {
      console.error("Error generating inventory share link:", error);
      toast.error(error instanceof Error ? error.message : "Failed to generate share link");
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: async () => {
      if (!currentShareData?.share_token) {
        throw new Error("No share token found to revoke");
      }

      // CRITICAL: Only delete sharing records, never modify inventory items
      const { error } = await supabase
        .from("public_inventory_shares")
        .delete()
        .eq("share_token", currentShareData.share_token);

      if (error) {
        console.error("Error revoking share:", error);
        throw new Error(`Failed to revoke share: ${error.message}`);
      }

      // Update user_inventory_shares to mark as revoked - ensure we only update for current user
      const { error: userShareError } = await supabase
        .from("user_inventory_shares")
        .update({
          is_public_shared: false,
          public_share_revoked: true,
        })
        .eq("user_id", userId)  // Explicitly filter by user_id for security
        .eq("public_share_token", currentShareData.share_token);  // And token for extra safety

      if (userShareError) {
        console.warn("Error updating user share preference:", userShareError);
      }
    },
    onSuccess: () => {
      toast.success("Inventory share link revoked successfully!");
      setShareUrl("");
      queryClient.invalidateQueries({ queryKey: ["user-inventory-share", userId] });
    },
    onError: (error) => {
      console.error("Error revoking inventory share link:", error);
      toast.error(error instanceof Error ? error.message : "Failed to revoke share link");
    },
  });

  const copyToClipboard = async () => {
    const urlToCopy = shareUrl || (currentShareData?.share_token ? 
      `${window.location.origin}/public/inventory/${currentShareData.share_token}` : "");
    
    if (!urlToCopy) {
      toast.error("No share link to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(urlToCopy);
      toast.success("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy link");
    }
  };

  const isShared = currentShareData?.is_shared && currentShareData?.share_token;
  const currentUrl = isShared 
    ? `${window.location.origin}/public/inventory/${currentShareData.share_token}`
    : "";

  const handleToggleShare = async (enabled: boolean) => {
    if (enabled) {
      generateShareMutation.mutate({ expiry: expiryDays });
    } else {
      revokeShareMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share className="h-5 w-5" />
            Share Your Inventory
          </DialogTitle>
          <DialogDescription>
            Generate a public link to share your personal inventory with anyone. This creates a READ-ONLY view that won't affect anyone's inventory.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Public Sharing</Label>
              <div className="text-sm text-muted-foreground">
                Allow anyone with the link to view your inventory (read-only)
              </div>
            </div>
            <Switch
              checked={Boolean(isShared || shareUrl)}
              onCheckedChange={handleToggleShare}
              disabled={generateShareMutation.isPending || revokeShareMutation.isPending}
            />
          </div>

          {/* Expiry Settings */}
          {(!isShared && !shareUrl) && (
            <div className="space-y-2">
              <Label htmlFor="expiry" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Link Expires
              </Label>
              <Select value={expiryDays} onValueChange={setExpiryDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Share URL Display */}
          {(isShared || shareUrl) && (
            <div className="space-y-2">
              <Label>Share Link</Label>
              <div className="flex gap-2">
                <Input
                  value={shareUrl || currentUrl}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={copyToClipboard}
                  className="shrink-0"
                  title="Copy link"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => window.open(shareUrl || currentUrl, '_blank')}
                  className="shrink-0"
                  title="Open link"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Expiry Info */}
          {currentShareData?.expiration && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
              <Calendar className="h-4 w-4" />
              <span>
                Expires on {new Date(currentShareData.expiration).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Security Notice */}
          <div className="flex items-start gap-2 text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <Shield className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">Security Notice</p>
              <p>This creates a READ-ONLY view of your inventory. No one can modify your items through this link.</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {(isShared || shareUrl) && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => revokeShareMutation.mutate()}
                disabled={revokeShareMutation.isPending}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Revoke Link
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="ml-auto"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
